import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Rating,
  Button,
  TextField,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { productAPI } from '../services/api';
import { addToCart } from '../store/slices/cartSlice';
import useApi from '../hooks/useApi';
import Loading from '../components/common/Loading';

const ProductDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [quantity, setQuantity] = useState(1);
  const { loading, execute: fetchProduct } = useApi(productAPI.getProduct);

  const [product, setProduct] = useState(null);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await fetchProduct(id);
      setProduct(response);
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error('Failed to load product details');
    }
  };

  const handleAddToCart = async () => {
    try {
      await productAPI.addToCart(id, quantity);
      dispatch(addToCart({ ...product, quantity }));
      toast.success('Product added to cart!');
    } catch (error) {
      toast.error('Failed to add product to cart');
    }
  };

  if (loading) {
    return <Loading message="Loading product details..." />;
  }

  if (!product) {
    return (
      <Container>
        <Typography variant="h6" color="error" sx={{ textAlign: 'center', py: 4 }}>
          Product not found
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={6}>
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </Grid>

          {/* Product Info */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h1" gutterBottom>
              {product.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Rating value={product.rating} readOnly />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({product.numReviews} reviews)
              </Typography>
            </Box>
            <Typography variant="h5" color="primary" gutterBottom>
              ${product.price.toFixed(2)}
            </Typography>
            <Typography variant="body1" paragraph>
              {product.description}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Status: {product.countInStock > 0 ? 'In Stock' : 'Out of Stock'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {product.countInStock} units available
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                type="number"
                label="Quantity"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1, max: product.countInStock }}
                sx={{ width: 100 }}
              />
              <Button
                variant="contained"
                size="large"
                onClick={handleAddToCart}
                disabled={product.countInStock === 0}
              >
                Add to Cart
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Product Details
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Category"
                  secondary={product.category}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Brand"
                  secondary={product.brand}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="SKU"
                  secondary={product.sku}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>

        {/* Reviews Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Reviews
          </Typography>
          <Grid container spacing={3}>
            {product.reviews?.map((review) => (
              <Grid item xs={12} key={review._id}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ mr: 1 }}>
                      {review.user.name}
                    </Typography>
                    <Rating value={review.rating} readOnly size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {new Date(review.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body1">{review.comment}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default ProductDetail; 